import { useState } from "react";
import { CheckCircle2, CalendarDays, Wallet, Video, Clock, ArrowRight } from "lucide-react";
import { COURSE_LIST } from "@/data/courses";
import BookingDialog from "@/components/site/BookingDialog";
import LpShell, { useLpMeta, GoogleStarsBadge } from "@/pages/lp/LpShell";
import LpLeadForm from "@/pages/lp/LpLeadForm";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";

const FEATURED_IDS = [
  "betreuungskraft",
  "ambulanter-pflegeassistent",
  "pflegehelfer-qn2",
  "wundexperte",
  "praxisanleitung",
  "pdl",
];

const USPS = [
  { icon: CalendarDays, text: "Start jeden Montag – keine Wartezeit" },
  { icon: Wallet, text: "Bildungsgutschein: meist 100 % gefördert" },
  { icon: Video, text: "100 % online live – von zu Hause aus" },
  { icon: CheckCircle2, text: "AZAV-zertifizierte Abschlüsse" },
];

export default function LpSelbstzahler() {
  useLpMeta(
    "Pflege-Weiterbildung · Start jeden Montag · Dickies-Campus",
    "Anerkannte Pflege-Weiterbildungen, 100 % online live. Mit Bildungsgutschein meist komplett gefördert. Start jeden Montag – jetzt kostenlos beraten lassen."
  );
  const [bookingCourse, setBookingCourse] = useState(null);
  const courses = FEATURED_IDS.map((id) => COURSE_LIST.find((c) => c.id === id)).filter(Boolean);

  return (
    <LpShell>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-12 lg:pt-20">
        <div className="max-w-3xl">
          <GoogleStarsBadge />
          <h1 data-testid="lp-b2c-headline" className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-sand-800 leading-[1.05]">
            Deine Pflege-Weiterbildung.<br />
            <span className="text-terra">Start jeden Montag.</span>
          </h1>
          <p className="mt-5 text-sand-600 text-base md:text-lg leading-relaxed max-w-xl">
            Anerkannte Abschlüsse – von Betreuungskraft bis PDL. 100 % online live,
            mit Bildungsgutschein meist <strong className="text-sand-800">komplett kostenlos</strong>.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
            {USPS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2.5 text-[14px] text-sand-700">
                <Icon className="h-5 w-5 text-terra shrink-0" strokeWidth={1.7} />
                {text}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#lp-kurse" data-testid="lp-b2c-cta-courses"
              className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-7 h-13 py-3.5 text-base font-semibold transition-colors group">
              Kurse ansehen
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
            </a>
            <a href="#lp-kontakt" data-testid="lp-b2c-cta-beratung"
              className="inline-flex items-center rounded-full border border-sand-400 hover:border-sand-800 text-sand-800 px-7 h-13 py-3.5 text-base font-medium transition-colors">
              Kostenlose Beratung
            </a>
          </div>
        </div>
      </section>

      {/* Kurse */}
      <section id="lp-kurse" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display text-3xl text-sand-800 mb-8">Unsere beliebtesten Weiterbildungen</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => (
            <div key={c.id} data-testid={`lp-course-${c.id}`}
              className="bg-white border border-cream-300 rounded-3xl p-7 flex flex-col shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all">
              <h3 className="font-display text-[1.3rem] text-sand-800 leading-snug">{c.title}</h3>
              <p className="text-[13px] text-sand-600 mt-1.5">{c.subtitle}</p>
              <div className="mt-4 space-y-1.5 text-[13px] text-sand-700">
                <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> {c.weeks} · {c.duration}</div>
                <div className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> Start: {c.nextStart}</div>
                <div className="flex items-center gap-2"><Wallet className="h-3.5 w-3.5 text-terra" strokeWidth={1.7} /> {c.funding}</div>
              </div>
              <div className="mt-5 pt-4 border-t border-cream-200 flex items-center justify-between">
                <span className="font-display text-xl font-semibold text-sand-800">{c.price}</span>
                <button type="button" data-testid={`lp-book-${c.id}`} onClick={() => setBookingCourse(c)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-terra hover:bg-terra-dark text-white px-5 py-2.5 text-[13px] font-semibold transition-colors">
                  Jetzt buchen <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-[13.5px] text-sand-600">
          Mit Bildungsgutschein der Agentur für Arbeit / des Jobcenters ist die Weiterbildung für dich meist <strong className="text-sand-800">komplett kostenlos</strong> – wir beraten dich zur Beantragung.
        </p>
      </section>

      {/* 3 Schritte */}
      <section className="bg-cream-100 border-y border-cream-300 py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl text-sand-800 mb-8">In 3 Schritten zum Abschluss</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["Kostenlose Beratung", "Wir klären gemeinsam, welcher Kurs zu dir passt – und ob du den Bildungsgutschein bekommst."],
              ["Anmeldung & Förderung", "Wir helfen bei den Unterlagen für Agentur für Arbeit oder Jobcenter. Ohne Gutschein: einfach direkt buchen."],
              ["Start am nächsten Montag", "100 % online live per Zoom – mit echten Dozentinnen, Materialien und persönlicher Betreuung bis zur Prüfung."],
            ].map(([t, d], i) => (
              <div key={t} className="bg-white rounded-3xl border border-cream-300 p-7">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-terra text-white font-bold text-sm">{i + 1}</span>
                <h3 className="font-display text-xl text-sand-800 mt-4">{t}</h3>
                <p className="text-[13.5px] text-sand-600 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kontakt */}
      <section id="lp-kontakt" className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl text-sand-800 leading-tight">
              Unsicher, welcher Kurs passt?<br /><span className="text-terra">Wir beraten dich kostenlos.</span>
            </h2>
            <p className="mt-4 text-sand-600 text-[15px] leading-relaxed max-w-md">
              Über 1.000 Pflegekräfte haben wir bereits qualifiziert. Wir sagen dir ehrlich,
              welcher Weg für dich der richtige ist – und übernehmen den Papierkram für die Förderung.
            </p>
            <TrackedPhoneLink source="lp-selbstzahler" className="mt-6 inline-flex items-center gap-2 text-lg font-semibold text-sand-800 hover:text-terra">
              📞 0211 29273330
            </TrackedPhoneLink>
          </div>
          <LpLeadForm source="ads-lp-selbstzahler" audience="pflegekraft"
            sub="Sag uns kurz, wofür du dich interessierst – wir melden uns mit einer ehrlichen Empfehlung." />
        </div>
      </section>

      <BookingDialog course={bookingCourse} open={!!bookingCourse} onOpenChange={(v) => !v && setBookingCourse(null)}
        initialBuyerType="selbstzahler" />
    </LpShell>
  );
}
